import { z } from "zod";
import { isValidCmsLink } from "@/lib/cms/link";
import { isValidAdminImageRef, ADMIN_IMAGE_URL_MESSAGE } from "@/lib/admin/media-url";

const campaignDate = z.string().datetime({ offset: true }).nullable();
export const promotionSchema = z.object({
  enabled: z.boolean(),
  heading: z.string().trim().max(160),
  description: z.string().trim().max(500),
  buttonText: z.string().trim().max(40),
  destination: z.string().trim().max(2048).refine(v => !v || isValidCmsLink(v), "Enter a path starting with / or a full https:// URL"),
  images: z.array(z.string().trim().max(2048).refine(isValidAdminImageRef, ADMIN_IMAGE_URL_MESSAGE)).max(3),
  startsAt: campaignDate,
  endsAt: campaignDate,
}).strict().superRefine((v, ctx) => {
  if (v.startsAt && v.endsAt && Date.parse(v.endsAt) <= Date.parse(v.startsAt)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["endsAt"], message: "End date must be after start date" });
  }
  if (v.enabled && (!v.heading || !v.description || !v.buttonText || !v.destination)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["enabled"], message: "Complete the heading, description, button and destination before enabling" });
  }
});
export type HomepagePromotion = z.infer<typeof promotionSchema>;

// Admin-only initial draft; public rendering never falls back to offer copy.
export const INITIAL_PROMOTION: HomepagePromotion = {
  enabled: false,
  heading: "GET 20% OFF YOUR FIRST ORDER!",
  description: "Enjoy fresh, home-style meals delivered across Mumbai.",
  buttonText: "ORDER NOW",
  destination: "https://poonamkapur.com/products",
  images: [], startsAt: null, endsAt: null,
};

export function parsePromotion(raw: unknown): HomepagePromotion | null {
  const result = promotionSchema.safeParse(raw);
  return result.success ? result.data : null;
}

export function visiblePromotion(raw: unknown, now: number): HomepagePromotion | null {
  const promo = parsePromotion(raw);
  if (!promo?.enabled) return null;
  if (promo.startsAt && Date.parse(promo.startsAt) > now) return null;
  if (promo.endsAt && Date.parse(promo.endsAt) <= now) return null;
  return promo;
}

export function toLocalCampaignDate(value: string | null): string {
  if (!value) return "";
  const date = new Date(value);
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

export function fromLocalCampaignDate(value: string): string | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date.toISOString() : null;
}